import { useState } from "react";
import { ClipboardDocumentIcon } from "@heroicons/react/24/outline";
import useSendingDomains from "../../data/useSendingDomains";
import { ISendingDomain, ISendingDomainDnsRecord } from "../../models/SendingDomain";
import useNotification from "../notifications/useNotifications";
import ConfirmModal from "../shared/Confirm/ConfirmModal";
import LoadingWheel from "../shared/LoadingWheel";
import Pill from "../shared/Pill";
import { ButtonInput } from "../shared/Inputs/ButtonInput";

/**
 * Lets the customer register the domains they want their quote emails sent from, shows the CNAME
 * records they need to add at their DNS provider, and re-checks verification on demand. Once a
 * domain is verified it can be attached to a website in the website editor.
 */
export default function SendingDomainsCard() {
  const { sendingDomains, addSendingDomain, verifySendingDomain, removeSendingDomain } = useSendingDomains();
  const { addNotification } = useNotification();
  const [domain, setDomain] = useState("");
  const [pendingRemove, setPendingRemove] = useState<ISendingDomain | null>(null);

  const notifyError = (err: unknown, fallback: string) =>
    addNotification({
      variant: "error",
      primaryText: err instanceof Error && err.message ? err.message : fallback,
    });

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    addNotification({ variant: "success", primaryText: "Copied to clipboard" });
  };

  const handleAdd = () => {
    const value = domain.trim();
    if (!value) return;

    addSendingDomain.mutate(
      { domain: value },
      {
        onSuccess: () => {
          setDomain("");
          addNotification({
            variant: "success",
            primaryText: "Sending domain added",
            secondaryText: "Add the DNS records below, then click Verify.",
          });
        },
        onError: (err) => notifyError(err, "Failed to add sending domain"),
      }
    );
  };

  const handleVerify = (id: string) => {
    verifySendingDomain.mutate(id, {
      onSuccess: (res) => {
        addNotification({
          variant: res.data?.verified ? "success" : "warn",
          primaryText: res.message,
        });
      },
      onError: (err) => notifyError(err, "Failed to verify sending domain"),
    });
  };

  const handleRemove = () => {
    if (!pendingRemove) return;

    removeSendingDomain.mutate(pendingRemove.id, {
      onSuccess: () => {
        setPendingRemove(null);
        addNotification({ variant: "success", primaryText: "Sending domain removed" });
      },
      onError: (err) => notifyError(err, "Failed to remove sending domain"),
    });
  };

  const rows = sendingDomains.data?.data ?? [];

  return (
    <div className="w-full bg-white border rounded-md p-4 mt-4">
      <h3 className="text-lg font-semibold text-gray-900">Email sending domains</h3>
      <p className="text-sm text-gray-600 mt-1">
        Quote emails are sent from the Wilma platform address unless you verify your own domain. Add
        your domain, create the CNAME records below at your DNS provider, then click Verify. Once
        verified, attach the domain to a website below and its emails will come from your address.
      </p>

      <div className="flex flex-col gap-2 sm:flex-row sm:items-end mt-4">
        <div className="flex-1">
          <label className="text-sm font-medium text-gray-500">Domain</label>
          <input
            type="text"
            value={domain}
            onChange={(e) => setDomain(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                handleAdd();
              }
            }}
            placeholder="example.com"
            className="w-full mt-1 text-sm text-gray-900 border-1 border-gray-300 rounded-md shadow-sm"
          />
        </div>
        <ButtonInput
          isSubmit={false}
          label={addSendingDomain.isLoading ? "Adding..." : "Add domain"}
          onClick={handleAdd}
          disabled={!domain.trim() || addSendingDomain.isLoading}
          classes="bg-green-600 hover:bg-green-700 h-[38px]"
        />
      </div>

      <div className="mt-4">
        {sendingDomains.isLoading ? (
          <LoadingWheel />
        ) : sendingDomains.isError ? (
          <p className="text-sm text-red-600">
            {sendingDomains.error instanceof Error ? sendingDomains.error.message : "Failed to load sending domains"}
          </p>
        ) : rows.length === 0 ? (
          <p className="text-sm text-gray-500">No sending domains yet.</p>
        ) : (
          <div className="space-y-4">
            {rows.map((row) => (
              <div key={row.id} className="rounded-md border border-gray-200 p-3 space-y-3">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <div className="flex items-center gap-2">
                    <p className="text-sm font-medium text-gray-900">{row.domain}</p>
                    <Pill label={row.verified ? "Verified" : "Pending DNS"} tone={row.verified ? "green" : "amber"} />
                  </div>
                  <div className="flex gap-2">
                    <ButtonInput
                      isSubmit={false}
                      label="Verify"
                      onClick={() => handleVerify(row.id)}
                      disabled={verifySendingDomain.isLoading}
                      classes="text-xs px-2 py-1"
                    />
                    <ButtonInput
                      isSubmit={false}
                      label="Remove"
                      onClick={() => setPendingRemove(row)}
                      classes="bg-red-600 hover:bg-red-700 text-xs px-2 py-1"
                    />
                  </div>
                </div>

                {row.dnsRecords?.length ? (
                  <DnsRecordsTable records={row.dnsRecords} rowId={row.id} onCopy={copyToClipboard} />
                ) : null}
              </div>
            ))}
          </div>
        )}
      </div>

      <ConfirmModal
        open={!!pendingRemove}
        setOpen={(open: boolean) => {
          if (!open) setPendingRemove(null);
        }}
        title="Remove sending domain"
        message={`Remove ${pendingRemove?.domain ?? ""}?\n\nWebsites using this domain will go back to sending from the Wilma platform address.`}
        confirmButtonText="Remove"
        onConfirm={handleRemove}
        isLoading={removeSendingDomain.isLoading}
      />
    </div>
  );
}

function DnsRecordsTable({
  records,
  rowId,
  onCopy,
}: {
  records: ISendingDomainDnsRecord[];
  rowId: string;
  onCopy: (text: string) => void;
}) {
  return (
    <div className="overflow-x-auto">
      <table className="min-w-full text-left text-sm">
        <thead>
          <tr className="text-xs font-medium uppercase text-gray-500">
            <th className="py-1 pr-3">Purpose</th>
            <th className="py-1 pr-3">Type</th>
            <th className="py-1 pr-3">Host</th>
            <th className="py-1 pr-3">Value</th>
            <th className="py-1">Status</th>
          </tr>
        </thead>
        <tbody>
          {records.map((record) => (
            <tr key={`${rowId}-${record.purpose}-${record.host}`}>
              <td className="py-1 pr-3 text-gray-700">{record.purpose}</td>
              <td className="py-1 pr-3 text-gray-700">{record.type}</td>
              <td className="py-1 pr-3">
                <CopyableValue value={record.host} onCopy={onCopy} />
              </td>
              <td className="py-1 pr-3">
                <CopyableValue value={record.value} onCopy={onCopy} />
              </td>
              <td className="py-1 text-xs text-gray-700">
                {record.verified ? "Verified" : record.status || "Pending"}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function CopyableValue({ value, onCopy }: { value: string; onCopy: (text: string) => void }) {
  return (
    <div className="flex items-center gap-1 font-mono text-xs">
      <span className="break-all">{value}</span>
      <button
        type="button"
        title="Copy"
        onClick={() => onCopy(value)}
        className="p-1 rounded hover:bg-gray-100 text-gray-500 hover:text-indigo-600"
      >
        <ClipboardDocumentIcon className="h-4 w-4" />
      </button>
    </div>
  );
}
